import PropTypes from "prop-types";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";

import { useCart } from "context/CartContext";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import VbsLogo from "./VbsLogo";
import VbsIcon from "./VbsIcon";
import { VBS, vbsCssVars, btnPrimary, resetButton } from "../theme";

function toKey(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

export default function StorefrontLayout({ children, maxWidth, hideHeader }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemCount } = useCart();
  const { token, pluginRole } = useAuth();
  const { tenant } = useStorefrontTenant();
  const { t } = useStorefrontCopy();

  const role = toKey(pluginRole);
  const portalUser = isPortalRole(role);
  const distributor = isDistributorRole(role);
  const isAuthed = !!token;
  const brandName = String(tenant?.name || t("common.brandFallback", "Valve Body Specialties")).trim();

  const isMobileRoute =
    location.pathname === "/m" || location.pathname.startsWith("/m/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  const [anchorEl, setAnchorEl] = useState(null);

  const goToSessionDestination = () => {
    if (!isAuthed) {
      navigate(toPath("/authentication/sign-in"));
      return;
    }
    if (portalUser) {
      navigate(distributor ? "/account/valve-bodies" : "/account");
      return;
    }
    navigate("/post-login");
  };

  const navLinks = [
    { label: t("common.home", "Home"), path: "/" },
    { label: t("common.catalog", "Products"), path: "/catalog" },
    ...(isAuthed
      ? []
      : [{ label: t("common.register", "Register"), path: "/register" }]),
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === toPath("/");
    return location.pathname.startsWith(toPath(path));
  };

  const pageBody =
    maxWidth === null ? (
      children
    ) : (
      <Container maxWidth={maxWidth || "xl"} sx={{ py: { xs: 3, md: 4 } }}>
        {children}
      </Container>
    );

  return (
    <Box
      sx={{
        ...vbsCssVars,
        minHeight: "100vh",
        bgcolor: VBS.canvas,
        color: VBS.ink,
        fontFamily: VBS.fontDisplay,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      {!hideHeader ? (
        <Box component="header" sx={{ position: "sticky", top: 0, zIndex: 1200 }}>
          {/* Utility strip — unlike EDS plain white header */}
          <Box
            sx={{
              background: VBS.swoosh,
              color: "#fff",
              minHeight: VBS.headerTop,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Container maxWidth="xl">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                <Box component="span">
                  {t("common.headerTagline", "Precision remanufacturing")}
                </Box>
                <Box component="span" sx={{ display: { xs: "none", sm: "block" }, opacity: 0.9 }}>
                  {t("common.utilityStrip", "Valve bodies · Cores · Warranty support")}
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Main bar — boxed nav tabs, no inline search (EDS has pill search here) */}
          <Box
            sx={{
              bgcolor: VBS.surface,
              borderBottom: `2px solid ${VBS.lineStrong}`,
              boxShadow: "0 4px 12px rgba(17,19,24,0.04)",
            }}
          >
            <Container maxWidth="xl">
              <Box
                sx={{
                  minHeight: VBS.headerMain,
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 2, md: 3 },
                }}
              >
                <Box
                  onClick={() => navigate(toPath("/"))}
                  sx={{ cursor: "pointer", flexShrink: 0 }}
                >
                  <VbsLogo variant="header" />
                </Box>

                <Stack
                  direction="row"
                  spacing={0}
                  sx={{ display: { xs: "none", md: "flex" }, flex: 1 }}
                >
                  {navLinks.map((link) => {
                    const active = isActive(link.path);
                    return (
                      <Box
                        key={link.path}
                        component="button"
                        type="button"
                        onClick={() => navigate(toPath(link.path))}
                        sx={{
                          ...resetButton,
                          px: 2,
                          py: 2.25,
                          fontSize: "0.8125rem",
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: active ? VBS.accent : VBS.muted,
                          borderBottom: active ? `3px solid ${VBS.accent}` : "3px solid transparent",
                          mb: "-2px",
                          transition: "color 160ms ease",
                          "&:hover": { color: VBS.accent },
                        }}
                      >
                        {link.label}
                      </Box>
                    );
                  })}
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: "auto" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigate(toPath("/cart"))}
                    sx={{
                      ...resetButton,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.25,
                      py: 0.75,
                      border: `1px solid ${VBS.lineStrong}`,
                      borderRadius: `${VBS.radius}px`,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: VBS.ink,
                    }}
                  >
                    <Badge
                      badgeContent={itemCount || 0}
                      invisible={!itemCount}
                      sx={{
                        "& .MuiBadge-badge": {
                          bgcolor: VBS.accent,
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.625rem",
                          minWidth: 16,
                          height: 16,
                        },
                      }}
                    >
                      <VbsIcon name="cart" size={18} color={VBS.ink} />
                    </Badge>
                    {t("common.cart", "Cart")}
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={goToSessionDestination}
                    sx={{
                      ...btnPrimary,
                      display: { xs: "none", sm: "block" },
                      py: 1,
                      px: 2,
                    }}
                  >
                    {isAuthed ? t("common.account", "Account") : t("common.signIn", "Sign in")}
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    aria-label={t("common.menu", "Menu")}
                    sx={{
                      ...resetButton,
                      display: { xs: "grid", md: "none" },
                      placeItems: "center",
                      p: 0.5,
                      border: `1px solid ${VBS.lineStrong}`,
                      borderRadius: `${VBS.radius}px`,
                    }}
                  >
                    <VbsIcon name="menu" size={22} color={VBS.ink} />
                  </Box>
                </Stack>
              </Box>
            </Container>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 0.5,
                minWidth: 200,
                borderRadius: `${VBS.radiusMd}px`,
                border: `1px solid ${VBS.lineStrong}`,
              },
            }}
          >
            {navLinks.map((link) => (
              <MenuItem
                key={link.path}
                onClick={() => {
                  setAnchorEl(null);
                  navigate(toPath(link.path));
                }}
                sx={{ fontSize: "0.875rem", fontWeight: 600 }}
              >
                {link.label}
              </MenuItem>
            ))}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                goToSessionDestination();
              }}
            >
              {isAuthed ? t("common.account", "Account") : t("common.signIn", "Sign in")}
            </MenuItem>
          </Menu>
        </Box>
      ) : null}

      <Box component="main">{pageBody}</Box>

      {/* Dark industrial footer — EDS uses light minimal footer */}
      <Box
        component="footer"
        sx={{
          mt: 8,
          bgcolor: VBS.footer,
          color: "#fff",
          borderTop: "none",
          "&::before": {
            content: '""',
            display: "block",
            height: 4,
            background: VBS.swoosh,
          },
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 5, md: 6 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 1fr" },
              gap: { xs: 4, md: 6 },
            }}
          >
            <Box>
              <Box sx={{ filter: "brightness(0) invert(1)", display: "inline-block" }}>
                <VbsLogo variant="footer" />
              </Box>
              <Box
                component="p"
                sx={{
                  mt: 2,
                  mb: 0,
                  fontSize: "0.875rem",
                  lineHeight: 1.7,
                  color: VBS.footerMuted,
                  maxWidth: 340,
                }}
              >
                {t(
                  "common.footerBlurb",
                  "Remanufactured valve bodies for transmission shops — measured, calibrated, and backed by warranty registration in your portal.",
                )}
              </Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.12em", color: VBS.accentBright, mb: 1.5 }}>
                {t("common.shopSection", "SHOP")}
              </Box>
              {[
                [t("common.catalog", "Products"), "/catalog"],
                [t("common.cart", "Cart"), "/cart"],
                ...(isAuthed ? [] : [[t("common.register", "Register"), "/register"]]),
              ].map(([label, path]) => (
                <Box
                  key={path}
                  component="button"
                  type="button"
                  onClick={() => navigate(toPath(path))}
                  sx={{
                    ...resetButton,
                    display: "block",
                    color: VBS.footerMuted,
                    mb: 0.75,
                    fontSize: "0.875rem",
                    textAlign: "left",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
            <Box>
              <Box sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.12em", color: VBS.accentBright, mb: 1.5 }}>
                {t("common.legalSection", "LEGAL")}
              </Box>
              {[
                [t("common.privacy", "Privacy"), "/privacy"],
                [t("common.deleteAccount", "Delete account"), "/delete-account"],
              ].map(([label, path]) => (
                <Box
                  key={path}
                  component="button"
                  type="button"
                  onClick={() => navigate(toPath(path))}
                  sx={{
                    ...resetButton,
                    display: "block",
                    color: VBS.footerMuted,
                    mb: 0.75,
                    fontSize: "0.875rem",
                    textAlign: "left",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              mt: 5,
              pt: 2.5,
              borderTop: "1px solid rgba(255,255,255,0.1)",
              fontSize: "0.75rem",
              color: VBS.footerMuted,
            }}
          >
            © {new Date().getFullYear()} {brandName}. {t("common.footerRights", "All rights reserved.")}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

StorefrontLayout.propTypes = {
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  hideHeader: PropTypes.bool,
};

StorefrontLayout.defaultProps = {
  maxWidth: "xl",
  hideHeader: false,
};
